import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from "@angular/core";
import {Menu} from "primeng/menu";
import {ConfirmationService, ConfirmEventType, MenuItem, MessageService,} from "primeng/api";
import {MvsFilterHelper} from "../../helper/mvs-filter-helper";
import {iObject} from "../../../command/component/search-dropdown-component/search-dropdown.component";
import {FilterCriteria} from "../../api/filter.criteria";
import {FilterCriteriaChangeEvent} from "../../api/filter-criteria-change-event";
import {FilterCriteriaUi} from "../../api/filter-criteria-ui";
import {MvsFilterComponentBaseAttribute} from "../../logic/mvs-filter-component-base-attribute";
import {MvsFormDto} from "../../../dto/form/dto/mvs-form.dto";
import {FilterAttributeDateTypeEnum} from "../../enum/filter-attribute-date-type.enum";
import {MvsCoreService} from "../../../service/mvs-core.service";
import {MetaService} from "../../../meta/service/meta.service";
import {DtoTemplate} from "../../../dto/dto-template";
import {JoinTypeEnum} from "../../enum/join-type.enum";
import {FilterCriteriaUiType} from "../../enum/filter-criteria-ui-type.enum";
import {WidgetData} from "../../../widget/widget-data";
import {
    FILTER_OPERATION_OPTIONS,
    getOperationLabel as sharedGetOperationLabel
} from "../../api/filter-operation-options";

@Component({
  selector: "mvs-condition-builder",
  templateUrl: "./condition-builder.component.html",
  styleUrls: ["./condition-builder.component.scss"],
  standalone: false
})
export class ConditionBuilderComponent extends MvsFilterComponentBaseAttribute implements OnInit, OnChanges, OnDestroy {
  @ViewChild("popupMenu") popupMenu!: Menu;
  @ViewChild("manual") manual!: any;
  busy: boolean = false;
  initialized: boolean = false;

  @Input() showLabels = true;
  @Input() objectTypeAlias: string;
  @Input() form: MvsFormDto;
  @Input() widgetData: WidgetData;
  @Input() invalidCriteriaLabel: string = null;

  // contains the FilterCriteria with a root record
  filterCriteriaUiRoot: FilterCriteriaUi | undefined;

  // contains a list of filter Criteria (for rendering) referencing the filterCriteriaUi nested list
  filterCriteriaUiList: FilterCriteriaUi[] | undefined;

  filterCriteriaClone: FilterCriteria[];

  selectedCriteria!: FilterCriteriaUi;
  popUpType: string = '';

  json: string = "";

  popupMenuItems: (MenuItem & { name: string | undefined })[] = [];
  attributeItems: (MenuItem & { name: string | undefined })[] = [];
  availableAttributeItems: (MenuItem & { name: string | undefined })[] = [];
  operationItems: (MenuItem & { name: string | undefined })[] = [];
  conditionItems: (MenuItem & { name: string | undefined })[] = [];

  filterAttributeDateTypeEnum: typeof FilterAttributeDateTypeEnum = FilterAttributeDateTypeEnum;

  dateFilterOptions: { label: string, value: string }[] = [
    {label: 'Bestimmter Tag', value: 'specificDay'},
    {label: 'Aktuelles Jahr', value: 'currentYear'},
    {label: 'Aktueller Monat', value: 'currentMonth'},
    {label: 'Aktueller Tag', value: 'currentDay'},
    {label: 'Aktuelle Woche', value: 'currentWeek'},
    {label: 'Bestimmtes Jahr', value: 'specificYear'},
    {label: 'Bestimmter Monat', value: 'specificMonth'},
    {label: 'Datumsbereich', value: 'dateRange'},
  ];

  booleanOptions: { label: string, value: string }[] = [
    {label: 'Wahr', value: 'true'},
    {label: 'Falsch', value: 'false'},
  ];

  levelColors: { [level: number]: string } = {
    0: 'primary-700',
    1: 'cyan-400',
    2: 'indigo-500',
    3: 'orange-300',
    4: 'green-500',
    5: 'red-500',
    6: 'blue-500',
    7: 'yellow-800',
    8: 'pink-300',
    9: 'purple-300',
    10: 'teal-300',
    11: 'bluegray-300',
    12: 'indigo-300',
    13: 'orange-800',
    14: 'teal-800',
    15: 'purple-400',
  };

  cascadeNodes: any = [];
  selectedAttribute: any;

  /**
   * Needs to be triggered whenever the filterCriteria change
   */

  @Output() filterValidation: EventEmitter<boolean> =
      new EventEmitter<boolean>();

  constructor(
      private confirmationService: ConfirmationService,
      private messageService: MessageService,
      private coreService: MvsCoreService,
      private metaService: MetaService) {
    super();
  }

  onNodeExpand(event: any) {
    const node = event.node;

    // Only load children if they haven't been loaded yet
    if (!node.children || node.children.length === 0) {
      const relevantForm = this.getFormFromObjectType(node.label);
      node.children = MvsFilterHelper.createEntityNodes(relevantForm, node.data.joinedAttribute + '.', this.widgetData);
    }
  }

  getFormFromObjectType(objectType: string, form: MvsFormDto = this.form): MvsFormDto | null {
    if (form.objectType === objectType) {
      return form;
    }
    for (const subFormKey in form.subForms) {
      const subForm = form.subForms[subFormKey];

      const foundForm = this.getFormFromObjectType(objectType, subForm);

      if (foundForm) {
        return foundForm;
      }
    }
    return null;
  }

  getSubForm(joinedAttribute: string, form: MvsFormDto = this.form) {
    if (!form.subForms || !Object.keys(form.subForms).length) {
      return null;
    }

    for (let key in form.subForms) {
      if (key == joinedAttribute) {
        return form.subForms[key];
      } else {
        const result = this.getSubForm(joinedAttribute, form.subForms[key]);
        if (result) {
          return result;
        }
      }
    }
    return null;

  }

  onNodeSelect(event: any) {
    if (event.node.leaf == false) {
      this.selectedAttribute = null;
      return;
    }
  }

  ngOnInit(): void {
    if (this.form) {
      this.cascadeNodes = MvsFilterHelper.buildCascadeSelectNodes(this.form, this.widgetData);
      // this.primengConfig.setTranslation(MvsConfigService.germanConfig);
      this.compPostInit();
    } else {
      const crudService = this.coreService.getCrudService(this.objectTypeAlias);
      crudService.template(new DtoTemplate()).subscribe((template) => {
        this.form = template;
        this.cascadeNodes = MvsFilterHelper.buildCascadeSelectNodes(this.form, this.widgetData);
        // this.primengConfig.setTranslation(MvsConfigService.germanConfig);
        this.compPostInit();
      })
    }

  }

  compPostInit() {
    this.initComponent();
    this.refreshComponent();
  }

  /**
   * Initialize Component.
   */
  initComponent() {

    this.operationItems = FILTER_OPERATION_OPTIONS.map(opt => ({
      label: opt.label,
      name: opt.value,
      command: (event: any) => {
        this.setCriteria(event, {operation: true});
      },
    }));

    this.conditionItems = [
      {
        label: "AND",
        name: "AND",
        command: () => {
          this.addCriteria(JoinTypeEnum.and);
        },
      },
      {
        label: "OR",
        name: "OR",
        command: () => {
          this.addCriteria(JoinTypeEnum.or);
        },
      },
      {
        label: "(",
        name: "(",
        command: () => {
          this.addCriteria(JoinTypeEnum.bracket);
        },
      },
    ];
  }

  onToggleJoin(filterCriteria: FilterCriteriaUi) {

    if (filterCriteria.or && filterCriteria.or.length > 0) {
      this.changeJoin(filterCriteria, JoinTypeEnum.and);
    } else {
      this.changeJoin(filterCriteria, JoinTypeEnum.or);
    }

    this.refreshUi();

  }

  // change the join type
  changeJoin(filterCriteria: FilterCriteriaUi, joinType: JoinTypeEnum) {

    if (joinType === JoinTypeEnum.or) {
      filterCriteria.or = filterCriteria.and;
      filterCriteria.and = [];
    } else {
      filterCriteria.and = filterCriteria.or;
      filterCriteria.or = [];
    }

  }

  /**
   * Add criteria.
   * @param joinType
   */
  addCriteria(joinType: JoinTypeEnum) {


    // @ts-ignore
    let parent: FilterCriteriaUi = (this.selectedCriteria.parent ? this.selectedCriteria.parent : this.filterCriteriaUiRoot);

    // @ts-ignore
    let filterCriteria = FilterCriteriaUi.createUi("", "", "", null, null, this.selectedCriteria.level, FilterCriteriaUiType.condition);

    if (joinType === JoinTypeEnum.bracket) {

      // increase level
      // @ts-ignore
      filterCriteria.level++;
      filterCriteria.uiType = FilterCriteriaUiType.join;

      if (parent.or && parent.or.length > 0) {
        // add to OR
        // parent.or.push(filterCriteria);
        this.selectedCriteria.or.push(filterCriteria);
      } else {
        // add to AND
        if (!parent.and) {
          parent.and = [];
        }
        // parent.and.push(filterCriteria);
        this.selectedCriteria.and.push(filterCriteria);
      }

      // add empty condition
      // @ts-ignore
      filterCriteria.and.push(FilterCriteriaUi.createUi("", "", "", null, null, filterCriteria.level, FilterCriteriaUiType.condition))


    } else {

      let joinTypeParent = null;

      // retrieve join type of parent
      if (parent.or && parent.or.length > 0) {
        joinTypeParent = JoinTypeEnum.or;
      } else {
        joinTypeParent = JoinTypeEnum.and;
      }

      if (joinTypeParent !== joinType) {
        // we need to change the join type
        this.changeJoin(parent, joinType);
      }

      if (joinType === JoinTypeEnum.and) {
        parent.and.push(filterCriteria);
      } else {
        parent.or.push(filterCriteria);
      }

    }

    this.refreshUi();

    this.handleChange();

  }

  onNodeDoubleClick(event): void {
    // Implement your logic for the double-click event on the selected node here
    const node = event;
    if (node.parent == undefined) {
      return;
    }


    // add attribute to first row of conditional builder
    if ((this.selectedCriteria.parent.and.length == 1 || this.selectedCriteria.parent.or.length == 1) && this.selectedCriteria.field == '') {

      if (node.leaf && node.parent) {
        // add and checks if the value attribute is selected and the first row is empty
        this.onAddValueAttributes(node);
        return;
      } else {
        this.onAddAttribute(node);
        return;
      }
    }

    // add attribute to conditional builder and checks for AND condition
    if (((this.selectedCriteria.parent.and.length > 1 || (this.selectedCriteria.parent.and.length == 1 && this.selectedCriteria.field != ''))) || (this.selectedCriteria.parent.and.length == 0 && this.selectedCriteria.parent.or.length == 0)) {

      this.addCriteria(JoinTypeEnum.and);
      this.selectedCriteria = this.filterCriteriaUiList[this.filterCriteriaUiList.length - 2];

      if (node.leaf && node.parent) {
        // add and checks if the value attribute is selected and AND condition is selected
        this.onAddValueAttributes(node);
        return;
      } else {
        this.onAddAttribute(node);
        return;
      }
    }

    // add attribute to conditional builder and checks for OR condition
    if ((this.selectedCriteria.parent.or.length > 1) || (this.selectedCriteria.parent.or.length == 1 && this.selectedCriteria.field != '')) {

      this.addCriteria(JoinTypeEnum.or);
      this.selectedCriteria = this.filterCriteriaUiList[this.filterCriteriaUiList.length - 2];

      if (node.leaf && node.parent) {
        // add and checks if the value attribute is selected and OR condition is selected
        this.onAddValueAttributes(node);
        return;
      } else {
        this.onAddAttribute(node);
        return;
      }
    }

  }

  onAddAttribute(node: any) {
    this.selectedCriteria.field = node.name ? node.name : node.label;
    this.selectedCriteria.label = node.label;
    this.selectedCriteria.dataType = node.dataType;
    this.selectedCriteria.joinObjectTypeAlias = node.joinObjectTypeAlias;
    this.selectedCriteria.joinObjectTypeId = node.joinObjectTypeId;
    this.popUpType = 'attributeItems';
    this.onAttributeItemsSelect();
    this.refreshUi();
  }

  onAddValueAttributes(node: any) {
    this.onAddAttribute(node.parent);
    this.selectedCriteria.value = node.value;
    const cascadeValue = {key: node.value, label: node.label}
    this.selectedCriteria.cascadeValueSelect = cascadeValue;

  }

  /**
   * Refresh Component.
   */
  refreshComponent() {
    this.initialized = true;

    // take over provided Array
    this.filterCriteriaClone = FilterCriteria.cloneFilterCriteriaArray(this.filterCriteria);

    // @ts-ignore
    this.convertWithRoot(this.filterCriteriaClone);

    this.selectedCriteria = this.filterCriteriaUiList[1];

  }

  /**
   * Process changes within Binding.
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized) {
      return;
    }

    // changes["filterCriteria"] ||
    if (changes["filterCriteria"] ||
        changes["filterAttributeGroups"] ||
        changes["readOnly"]
    ) {
      this.refreshComponent();
    }
  }

  /**
   * Destroy component.
   */
  ngOnDestroy(): void {
  }


  convertFilterCriteriaToUi(criterion: FilterCriteria): FilterCriteriaUi {
    if (this.form.formContents && Object.keys(this.form.formContents).length > 0) {
      return this._convertFilterCriteriaToQlUi(criterion);
    } else {
      return this._convertFilterCriteriaToListUi(criterion);
    }
  }


  /**
   * Add level and parent information to filter Criteria. The parent is required for navigation.
   * @param criteria
   * @param level
   * @param parent
   */

  convertWithRoot(criteria: FilterCriteria[]): void {
    this.filterCriteriaUiRoot = FilterCriteriaUi.createUi("", "", "", null, null, 0, FilterCriteriaUiType.join);

    if (criteria && criteria.length) {

      // Unwrap root wrapper: handleChange saves as [rootNode] where rootNode has field:"" and children in and/or
      let unwrapped = criteria;
      let useOr = true;
      while (unwrapped.length === 1 && !unwrapped[0].field && (unwrapped[0].and?.length || unwrapped[0].or?.length)) {
        const wrapper = unwrapped[0];
        useOr = wrapper.or?.length > 0;
        unwrapped = useOr ? wrapper.or : wrapper.and;
      }

      if (useOr) {
        this.filterCriteriaUiRoot.or = [];
        for (let criterion of unwrapped) {
          this.filterCriteriaUiRoot.or.push(this.convertFilterCriteriaToUi(criterion));
        }
      } else {
        this.filterCriteriaUiRoot.and = [];
        for (let criterion of unwrapped) {
          this.filterCriteriaUiRoot.and.push(this.convertFilterCriteriaToUi(criterion));
        }
      }
    } else {
      this.filterCriteriaUiRoot.or.push(FilterCriteriaUi.createUi("", "", FilterCriteria.cOperatorEqual, null, null, 0, FilterCriteriaUiType.condition));
    }

    this.refreshUi();
  }


  refreshUi() {
    this.filterCriteriaUiList = [];

    // @ts-ignore
    this.refreshUiFilterCriteria(this.filterCriteriaUiRoot, null, 0, 0, 0);
    // @ts-ignore
    this.filterCriteriaUiList.push(this.filterCriteriaUiRoot);

    // @ts-ignore
    this.refreshUiTreeJoins(this.filterCriteriaUiRoot, 0);

  }


  refreshUiTreeJoins(criterion: FilterCriteriaUi, level: number) {

    if (criterion.and && criterion.and.length > 0) {
      this.refreshUiTree(criterion, criterion.and, JoinTypeEnum.and, level);

    } else if (criterion.or && criterion.or.length > 0) {
      this.refreshUiTree(criterion, criterion.or, JoinTypeEnum.or, level);
    }
  }

  refreshUiFilterCriteria(criterion: FilterCriteriaUi, joinType: JoinTypeEnum, peerIndex: number, peerCount: number, level: number) {

    criterion.level = level;
    criterion.showAdd = false;
    criterion.showBracket = false;
    criterion.bracket = "";
    criterion.joinOperator = "";
    criterion.showJoinOperator = false;

    if (criterion.uiType == FilterCriteriaUiType.dummyJoinEnd) {
      criterion.bracket = ")";
      criterion.showBracket = true;
    } else if (criterion.uiType == FilterCriteriaUiType.join) {

      // @ts-ignore
      if (criterion.level > 0) {
        criterion.showJoinOperator = true;
        criterion.joinOperator = (joinType === JoinTypeEnum.or ? "OR" : "AND");
      }
      criterion.showBracket = true;
      criterion.bracket = "(";

      criterion.showAdd = true;
    } else if (peerIndex > 0) {
      criterion.showJoinOperator = true;
      criterion.joinOperator = (joinType === JoinTypeEnum.or ? "OR" : "AND");
    } else {
      criterion.showJoinOperator = false;
      criterion.joinOperator = "";
    }

    if (criterion.uiType == FilterCriteriaUiType.condition) {
      if (peerIndex + 1 == peerCount) {
        criterion.showAdd = true;
      }
    }


    // calculate BG color
    // @ts-ignore
    const colorAdjust = 255 - (20 * criterion.level);
    criterion.bgColor = `rgba(${colorAdjust},${colorAdjust},${colorAdjust},1`;

    // calculate indent
    // @ts-ignore
    const indent: number = criterion.level * 3;
    criterion.indentPx = `${indent}px`;

  }

  refreshUiTree(parentCriteria: FilterCriteriaUi, criteria: FilterCriteriaUi[], joinType: JoinTypeEnum, level: number) {
    let index: number = 0;


    for (let criterion of criteria) {
      let mylevel: number = level;

      criterion.parent = parentCriteria;

      if (criterion.uiType === FilterCriteriaUiType.join) {
        mylevel = level + 1;
      }

      // update UI attributes
      this.refreshUiFilterCriteria(criterion, joinType, index, criteria.length, mylevel);

      // push myself
      this.filterCriteriaUiList?.push(criterion);
      this.refreshUiTreeJoins(criterion, mylevel);

      index++;
    }

    // create dummy filter entry to close the bracket
    // @ts-ignore
    let dummyJoinEnd = FilterCriteriaUi.createDummyJoinEnd(parentCriteria.level);
    this.refreshUiFilterCriteria(dummyJoinEnd, JoinTypeEnum.none, 0, 0, level);
    this.filterCriteriaUiList?.push(dummyJoinEnd);

  }

  onTreeSelectNodeSelect(criteria: FilterCriteriaUi, type: string, event: any) {
    if (!this.readOnly) {
      const node = event?.node;

      // only process leaf (field) nodes, ignore entity/parent nodes
      if (!node?.leaf || !node?.data) {
        return;
      }

      this.processTreeNodeSelection(criteria, type, node);
    }
  }

  private processTreeNodeSelection(criteria: FilterCriteriaUi, type: string, node: any) {
    this.selectedCriteria = criteria;
    this.popUpType = type;
    const nodeData = node.data;
    const criteriaEvent = {
      item: {
        field: nodeData.field,
        label: node.label,
        isJoin: nodeData.isJoin,
        objectType: nodeData.objectType,
        dataType: nodeData.dataType,
        joinObjectTypeId: nodeData.joinObjectTypeId,
        parentObjectType: node.parent?.label
      }
    };
    this.setCriteria(criteriaEvent, {field: true});
    this.onAttributeItemsSelect();

    // FIX 2: Ensure selectManual is correctly set based on dataType for newly added rows.
    // onAttributeItemsSelect may early-return if the form attribute is not found,
    // leaving selectManual at its default (false). Use the tree node's dataType as fallback.
    const dt = this.selectedCriteria.dataType;
    const isDateField = dt === FilterAttributeDateTypeEnum.date || dt === FilterAttributeDateTypeEnum.dateTime || dt === FilterAttributeDateTypeEnum.dateInstant;
    const isBooleanField = dt === FilterAttributeDateTypeEnum.boolean;
    const hasEnumValues = this.selectedCriteria.valueList && this.selectedCriteria.valueList.some(group => group.name === 'values' && group.items?.length > 0);

    if (hasEnumValues) {
      // Enum field (dataType may be boolean due to DataTypeHelper mapping): show dropdown directly
      this.selectedCriteria.selectManual = false;
      this.selectedCriteria.selectManualValueTo = false;
      this.selectedCriteria.isEnumField = true;
      this.selectedCriteria.isDateField = false;
    } else if (isDateField) {
      // Date field: show date option dropdown (Current Day, Current Year, Specific Day, etc.)
      this.selectedCriteria.selectManual = false;
      this.selectedCriteria.selectManualValueTo = false;
      this.selectedCriteria.isEnumField = false;
      this.selectedCriteria.isDateField = true;
    } else if (isBooleanField || !nodeData.isJoin) {
      this.selectedCriteria.selectManual = true;
      this.selectedCriteria.selectManualValueTo = true;
      this.selectedCriteria.isEnumField = false;
      this.selectedCriteria.isDateField = false;
    }

    this.handleChange();
  }

  setCriteria(event: any, options: { field?: boolean; operation?: boolean }) {

    const criteria = this.selectedCriteria;

    if (this.popUpType == 'attributeItems' && event?.item) {
      this.selectedCriteria.dataType = event.item.dataType;
      this.selectedCriteria.joinObjectTypeAlias = event.item.objectType;
      this.selectedCriteria.joinObjectTypeId = event.item.joinObjectTypeId;
      this.selectedCriteria.parentObjectType = event.item.parentObjectType;
      this.selectedCriteria['isJoin'] = event.item.isJoin;
    }

    if (criteria && event?.item) {
      if (options.field) {
        criteria.field = event.item.field;
        criteria.label = event.item.label;
      }
      if (options.operation) {
        criteria.operation = event.item.name;
      }
    }


  }


  togglePopupMenu(
      criteria: FilterCriteriaUi,
      items: (MenuItem & { name: string | undefined })[],
      event: any,
      type: string
  ) {
    if (!this.readOnly) {
      this.selectedCriteria = criteria;
      this.popupMenuItems = items;
      this.popupMenu.toggle(event);
      this.popUpType = type;
    }

  }

  onAttributeItemsSelect() {
    if (this.popUpType === 'attributeItems') {

      const entity = this.form.objectType;

      this.selectedCriteria.valueList = [];
      this.selectedCriteria.selectManual = false;

      this.selectedCriteria.value = '';
      this.selectedCriteria.cascadeValueSelect = null;

      // Clear date values when switching attributes to prevent NaN display
      this.selectedCriteria.dateValue = null;
      this.selectedCriteria.dateValueTo = null;

      let relevantForm: MvsFormDto;

      if (this.widgetData?.dataSource === 'ql') {
        relevantForm = this.form;
      } else {
        relevantForm = this.getFormFromObjectType(this.selectedCriteria.parentObjectType);
      }
      if (!relevantForm) {
        return;
      }


      const actualFieldPaths = this.selectedCriteria.field.split('.');
      let actualField: string;
      if (this.widgetData?.dataSource === 'ql') {
        actualField = this.selectedCriteria.field;
      } else {
        if (actualFieldPaths.length == 1) {
          actualField = actualFieldPaths[0];
        } else if (actualFieldPaths.length == 2) {
          actualField = actualFieldPaths[1];
        }
      }

      let attribute = relevantForm.getFormField(actualField);

      if (!attribute) {
        attribute = relevantForm.getFormField(actualField + 'DtoId');
      }

      // handle reports separately
      if (!entity) {
        actualField = this.selectedCriteria.field;

        attribute = relevantForm.getFormField(actualField);

      }

      if (!attribute) {
        return;
      }

      // const attribute = relevantForm.formFields[actualField];

      if (attribute.valueList?.entries?.length) {
        this.selectedCriteria.valueOption = 'key';
        const items = attribute.valueList.entries.map(item => {
          return {key: item.key, label: item.label, technicalLabel: item['technicalLabel']}
        })
        this.selectedCriteria.valueList.push({name: 'values', items: items});
      }

      // Add date runtime variables for date/dateTime/dateInstant fields
      const dt = this.selectedCriteria.dataType;
      if (dt === FilterAttributeDateTypeEnum.date || dt === FilterAttributeDateTypeEnum.dateTime || dt === FilterAttributeDateTypeEnum.dateInstant) {
        this.selectedCriteria.valueList.push({
          name: 'dates', items: [
            {name: '${D-TODAY}', label: 'Aktueller Tag', hasParameter: false},
            {name: '${D-STARTYEAR}', label: 'Jahresanfang', hasParameter: false},
            {name: '${D-ENDYEAR}', label: 'Jahresende', hasParameter: false},
            {name: '${D-STARTMONTH}', label: 'Monatsanfang', hasParameter: false},
            {name: '${D-ENDMONTH}', label: 'Monatsende', hasParameter: false},
            {name: '${D-STARTWEEK}', label: 'Wochenanfang', hasParameter: false},
            {name: '${D-ENDWEEK}', label: 'Wochenende', hasParameter: false},
          ]
        });
        this.selectedCriteria.valueOption = 'name';
      }

      this.selectedCriteria.valueList.push({
        name: 'manual'
      });

      // Auto-detect the correct value input based on field type and available options.
      // If the field has enum values in valueList (beyond 'manual' and 'dates' groups),
      // show the cascadeSelect dropdown. Otherwise show the appropriate manual input.
      const dtField = this.selectedCriteria.dataType;
      const isDateField = dtField === FilterAttributeDateTypeEnum.date || dtField === FilterAttributeDateTypeEnum.dateTime || dtField === FilterAttributeDateTypeEnum.dateInstant;
      const isBooleanField = dtField === FilterAttributeDateTypeEnum.boolean;
      const hasEnumValues = this.selectedCriteria.valueList.some(group => group.name === 'values' && group.items?.length > 0);

      if (hasEnumValues) {
        // Enum field (dataType may be boolean due to DataTypeHelper mapping): show dropdown directly
        this.selectedCriteria.selectManual = false;
        this.selectedCriteria.selectManualValueTo = false;
        this.selectedCriteria.isEnumField = true;
        this.selectedCriteria.isDateField = false;
      } else if (isDateField) {
        // Date field: show date option dropdown
        this.selectedCriteria.selectManual = false;
        this.selectedCriteria.selectManualValueTo = false;
        this.selectedCriteria.isEnumField = false;
        this.selectedCriteria.isDateField = true;
      } else {
        // Boolean, string, number: show manual input (toggle, text, number)
        this.selectedCriteria.selectManual = true;
        this.selectedCriteria.selectManualValueTo = true;
        this.selectedCriteria.isEnumField = false;
        this.selectedCriteria.isDateField = false;
      }
    }

    if (this.popUpType === 'operationItems') {
      if (this.selectedCriteria.operation != 'BT') {
        this.selectedCriteria.valueTo = null;
        this.selectedCriteria.variableNameValueTo = null;
        this.selectedCriteria.parameterValueTo = null;
      }
      this.handleChange();
    }
  }

  onRemoveCriteria(criteria: FilterCriteriaUi) {

    this.confirmationService.confirm({
      message: 'Möchten Sie diesen Eintrag löschen?',
      header: 'Löschen bestätigen',
      icon: 'pi pi-info-circle',
      accept: () => {

        // FIX 3: If this is a root-level condition and it's the ONLY remaining child, clear values instead of removing
        const rootChildren = this.getGroupChildren(this.filterCriteriaUiRoot);
        const isDirectRootChild = rootChildren.indexOf(criteria) >= 0;
        const isCondition = criteria.uiType === FilterCriteriaUiType.condition;

        if (isDirectRootChild && isCondition && rootChildren.length === 1) {
          // Clear values instead of removing — keep the row as an empty placeholder
          criteria.field = '';
          criteria.operation = '';
          criteria.value = null;
          criteria.valueTo = null;
          criteria.dateValue = null;
          criteria.dateValueTo = null;
          criteria.cascadeValueSelect = null;
          criteria.cascadeValueToSelect = null;
          criteria.label = '';
          criteria.selectManual = true;
          criteria.selectManualValueTo = true;
          criteria.valueList = [];
          criteria.hasParameter = false;
          criteria.hasParameterValueTo = false;
          criteria.parameterValue = null;
          criteria.parameterValueTo = null;
          criteria.variableNameValue = null;
          criteria.variableNameValueTo = null;
          this.refreshUi();
          this.handleChange();
          this.messageService.add({severity: 'info', summary: 'Bestätigt', detail: 'Werte zurückgesetzt'});
          return;
        }

        // Normal removal for non-last root conditions and all sub-group conditions
        if (this.filterCriteriaUiRoot.and.length) {
          this.onRemoveItem(criteria, this.filterCriteriaUiRoot.and)
        }
        if (this.filterCriteriaUiRoot.or.length) {
          this.onRemoveItem(criteria, this.filterCriteriaUiRoot.or)
        }

        this.messageService.add({severity: 'info', summary: 'Bestätigt', detail: 'Eintrag gelöscht'});

        this.handleChange();

      },
      reject: (type) => {
        switch (type) {
          case ConfirmEventType.REJECT:
            this.messageService.add({severity: 'error', summary: 'Abgelehnt', detail: 'Aktion abgelehnt'});
            break;
          case ConfirmEventType.CANCEL:
            this.messageService.add({severity: 'warn', summary: 'Abgebrochen', detail: 'Aktion abgebrochen'});
            break;
        }
      }
    });

  }

  onRemoveItem(criteria: FilterCriteriaUi, filterCriteriaRootArray: any) {

    // First check if the criteria itself is directly in the array (for JOIN nodes at root level)
    const directIndex = filterCriteriaRootArray.findIndex(item => item === criteria);
    if (directIndex >= 0) {
      console.log('[onRemoveItem] Found item directly in array at index:', directIndex);
      filterCriteriaRootArray.splice(directIndex, 1);
      this.refreshUi();
      return;
    }

    for (let entry of filterCriteriaRootArray) {
      // Check if this entry contains the criteria we're looking for (by reference)
      const foundInAnd = entry.and?.findIndex(item => item === criteria);
      const foundInOr = entry.or?.findIndex(item => item === criteria);

      if (foundInAnd !== undefined && foundInAnd >= 0) {
        // Found in AND array
        console.log('[onRemoveItem] Found in AND array, entry.and.length:', entry.and.length);
        if (entry.and.length == 1) {
          // Remove the entire parent join node (parentheses block)
          const parentIndex = filterCriteriaRootArray.indexOf(entry);
          if (parentIndex >= 0) {
            filterCriteriaRootArray.splice(parentIndex, 1);
          }
        } else {
          entry.and.splice(foundInAnd, 1);
        }
        this.refreshUi();
        return;
      }

      if (foundInOr !== undefined && foundInOr >= 0) {
        // Found in OR array
        console.log('[onRemoveItem] Found in OR array, entry.or.length:', entry.or.length);
        if (entry.or.length == 1) {
          // Remove the entire parent join node (parentheses block)
          const parentIndex = filterCriteriaRootArray.indexOf(entry);
          if (parentIndex >= 0) {
            filterCriteriaRootArray.splice(parentIndex, 1);
          }
        } else {
          entry.or.splice(foundInOr, 1);
        }
        this.refreshUi();
        return;
      }

      // Recurse into nested structures
      if (entry.and?.length) {
        this.onRemoveItem(criteria, entry.and);
      }
      if (entry.or?.length) {
        this.onRemoveItem(criteria, entry.or);
      }
    }

    console.log('[onRemoveItem] Item not found!');
  }

  onSelectManual(event: any, criteria: FilterCriteriaUi, type: string) {

    this.selectedCriteria = criteria;

    if (!event.value) {
      return;
    }

    if (type === 'value') {

      if (event.value.key == undefined) {
        if (!event.value.hasParameter) {
          this.selectedCriteria.value = event.value.name;
        }
        this.selectedCriteria.variableNameValue = event.value.name;
        this.selectedCriteria.hasParameter = event.value.hasParameter;
      }

      if (event.value.key != undefined) {
        if (this.enumValuesType == 'technicalLabel') {
          this.selectedCriteria.value = event.value.technicalLabel;
        } else {
          this.selectedCriteria.value = event.value.key
        }

      }

    }
    if (type === 'valueTo') {
      if (!event.value.key) {
        if (!event.value.hasParameter) {
          this.selectedCriteria.valueTo = event.value.name;
        }
        this.selectedCriteria.variableNameValueTo = event.value.name;
        this.selectedCriteria.hasParameterValueTo = event.value.hasParameter;
      }

      if (event.value.key) {
        this.selectedCriteria.valueTo = event.value.key
      }

    }

    if (event.value?.name == 'manual' || event.value == 'manual' || event.value == undefined) {
      if (type === 'value') {
        this.selectedCriteria.selectManual = true;
        this.selectedCriteria.value = null;
      } else if (type === 'valueTo') {
        this.selectedCriteria.selectManualValueTo = true;
        this.selectedCriteria.valueTo = null;
      }
    }
    this.handleChange()
  }


  /**
   * This method is always called whenever the filter is changed.
   */
  handleChange() {
    // something changed, so we need to make sure that it is valid and we need to inform the calling component

    // convert UI to regular FilerCriteria

    // checks whether filterCriteria belongs to AND condition or OR condition
    const filterCriteriaRoot = this.filterCriteriaUiRoot.and.length ? this.filterCriteriaUiRoot.and : this.filterCriteriaUiRoot.or;

    const stringFilterCriteria = this.filterCriteriaUiRoot.toString();

    // const changedFilterCriteria: FilterCriteria[] = FilterCriteriaUi.convertFromUi(filterCriteriaRoot);
    const changedFilterCriteria: FilterCriteria[] = FilterCriteriaUi.convertFromUi([this.filterCriteriaUiRoot]);

    // const validFilter = !filterValidation;
    const validFilter = this.isFilterValid(changedFilterCriteria);

    this.onChange.emit(new FilterCriteriaChangeEvent(changedFilterCriteria, validFilter));

  }

  isFilterValid(filterCriteria: FilterCriteria[]): boolean {

    for (const criteria of filterCriteria) {
      if (criteria.and.length) {
        // Recursively check nested AND array for validity
        if (!this.isFilterValid(criteria.and)) {
          return false;
        }
      } else if (criteria.or.length) {
        // Recursively check nested OR array for validity
        if (!this.isFilterValid(criteria.or)) {
          return false;
        }
      } else {
        // Check for attribute validity
        const isNullOperation = criteria.operation === FilterCriteria.cOperatorIsNull || criteria.operation === FilterCriteria.cOperatorIsNotNull;
        if (!criteria.field || !criteria.operation || ((criteria.value == null || criteria.value === '') && !isNullOperation) || (criteria.operation == 'BT' && (criteria.valueTo == null || criteria.valueTo === ''))) {
          return false;
        }

        if (criteria.operation == 'BT' && (criteria.value > criteria.valueTo)) {
          return false;
        }
      }
    }
    return true;
  }


  handleVariableParameter(criteria: FilterCriteriaUi, type: string) {
    this.selectedCriteria = criteria;
    if (type === 'value') {
      this.selectedCriteria.value = this.selectedCriteria.variableNameValue + '{' + this.selectedCriteria.parameterValue + '}';
    }
    if (type === 'valueTo') {
      this.selectedCriteria.valueTo = this.selectedCriteria.variableNameValueTo + '{' + this.selectedCriteria.parameterValueTo + '}';
    }
    this.handleChange();
  }

  onJoinSelect(criteria: FilterCriteriaUi, event: any) {
    criteria.value = event.objectId;
    this.handleChange();
  }

  handleDate(criteria: FilterCriteriaUi, option: string) {
    this.selectedCriteria = criteria;
    const rawDate = option === 'value' ? criteria.dateValue : criteria.dateValueTo;

    if (!rawDate) {
      if (option === 'value') {
        this.selectedCriteria.value = null;
      } else if (option === 'valueTo') {
        this.selectedCriteria.valueTo = null;
      }
      this.handleChange();
      return;
    }

    const selectedDate: Date = new Date(rawDate);
    if (isNaN(selectedDate.getTime())) {
      return;
    }

    const yyyy = selectedDate.getFullYear();
    const mm = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const dd = String(selectedDate.getDate()).padStart(2, '0');

    let date: string;
    const dt = this.selectedCriteria.dataType;

    if (dt === FilterAttributeDateTypeEnum.dateInstant) {
      const hh = String(selectedDate.getHours()).padStart(2, '0');
      const min = String(selectedDate.getMinutes()).padStart(2, '0');
      const ss = String(selectedDate.getSeconds()).padStart(2, '0');
      date = `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}.000000Z`;
    } else if (dt === FilterAttributeDateTypeEnum.dateTime) {
      const hh = String(selectedDate.getHours()).padStart(2, '0');
      const min = String(selectedDate.getMinutes()).padStart(2, '0');
      const ss = String(selectedDate.getSeconds()).padStart(2, '0');
      date = `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
    } else {
      date = `${yyyy}-${mm}-${dd}`;
    }

    if (option === 'value') {
      this.selectedCriteria.value = date;
    } else if (option === 'valueTo') {
      this.selectedCriteria.valueTo = date;
    }

    this.handleChange();
  }

  getColorByLevel(level: number): string {
    return this.levelColors[level];
  }

  /**
   * Check if a criteria row should be highlighted due to validation error
   */
  isCriteriaHighlighted(criteria: FilterCriteriaUi): boolean {
    return this.invalidCriteriaLabel !== null && criteria.label === this.invalidCriteriaLabel;
  }

  /**
   * Generate dynamic validation tooltip based on criteria state
   */
  getValidationTooltip(criteria: FilterCriteriaUi): string {
    const isNullOperation = criteria.operation === FilterCriteria.cOperatorIsNull || criteria.operation === FilterCriteria.cOperatorIsNotNull;

    if (!criteria.field) {
      return 'Bitte wählen Sie ein Attribut aus, um die Bedingung zu konfigurieren.';
    }

    if (!criteria.operation) {
      return 'Bitte wählen Sie einen Operator (z. B. Gleich, Enthält).';
    }

    if (isNullOperation) {
      return `✓ Vollständig: Für "${criteria.operation}" ist kein Wert erforderlich.`;
    }

    // Check if value is required but not provided
    const needsValue = (criteria.value == null || criteria.value === '') && !criteria.cascadeValueSelect;
    const needsValueTo = criteria.operation === 'BT' && (criteria.valueTo == null || criteria.valueTo === '') && !criteria.cascadeValueToSelect;

    if (needsValue && needsValueTo) {
      return 'Erforderlich: Bitte geben Sie beide Werte für die Zwischen-Operation an.';
    }

    if (needsValue) {
      return 'Erforderlich: Bitte geben Sie einen Wert ein, um die Bedingung abzuschließen.';
    }

    if (needsValueTo) {
      return 'Erforderlich: Bitte geben Sie den zweiten Wert für den Zwischen-Bereich ein.';
    }

    return '✓ Vollständig: Alle erforderlichen Felder sind ausgefüllt.';
  }

  /**
   * Check if a specific criteria row has validation errors
   */
  hasValidationError(criteria: FilterCriteriaUi): boolean {
    if (!criteria.field) return true;
    if (!criteria.operation) return true;

    const isNullOperation = criteria.operation === FilterCriteria.cOperatorIsNull || criteria.operation === FilterCriteria.cOperatorIsNotNull;

    if (!isNullOperation) {
      if ((criteria.value == null || criteria.value === '') && !criteria.cascadeValueSelect) return true;
      if (criteria.operation === 'BT' && (criteria.valueTo == null || criteria.valueTo === '') && !criteria.cascadeValueToSelect) return true;
    }

    return false;
  }

  /**
   * Get human-readable label for an operation code.
   */
  getOperationLabel(operation: string): string {
    return sharedGetOperationLabel(operation);
  }

  /**
   * Check if a condition row is partially filled (has at least one value set).
   * Used to determine whether to show validation warnings.
   */
  isPartiallyFilled(criteria: FilterCriteriaUi): boolean {
    return !!(criteria.field || criteria.operation || criteria.value || criteria.cascadeValueSelect);
  }

  /**
   * Check if field is missing — always shows warning for empty field rows.
   */
  isFieldMissing(criteria: FilterCriteriaUi): boolean {
    return !criteria.field;
  }

  /**
   * Check if operation is missing on a row that has a field selected.
   */
  isOperationMissing(criteria: FilterCriteriaUi): boolean {
    return !!criteria.field && !criteria.operation;
  }

  /**
   * Check if value is missing on a row that has field + operation but needs a value.
   */
  isValueMissing(criteria: FilterCriteriaUi): boolean {
    if (!criteria.field || !criteria.operation) return false;
    const isNullOp = criteria.operation === FilterCriteria.cOperatorIsNull || criteria.operation === FilterCriteria.cOperatorIsNotNull;
    if (isNullOp) return false;
    return (criteria.value == null || criteria.value === '') && !criteria.cascadeValueSelect;
  }

  /**
   * Check if a condition row is incomplete (missing any required part).
   */
  isConditionIncomplete(criteria: FilterCriteriaUi): boolean {
    if (!criteria.field && !criteria.value && !criteria.cascadeValueSelect) return false;
    return this.isFieldMissing(criteria) || this.isOperationMissing(criteria) || this.isValueMissing(criteria);
  }

  handleSelectObjectSearch(criteria: FilterCriteriaUi, event: iObject) {
    criteria.value = event.id;
    criteria.cascadeValueSelect = event;
    this.handleChange();
  }

  // ===== NEW HELPER METHODS FOR RECURSIVE TEMPLATE =====


  /** Check if a group uses AND logic (has children in .and[]) */
  isAndGroup(group: FilterCriteriaUi): boolean {
    return group.and && group.and.length > 0;
  }

  /** Get the active children array of a group */
  getGroupChildren(group: FilterCriteriaUi): FilterCriteriaUi[] {
    if (group.and && group.and.length > 0) return group.and;
    if (group.or && group.or.length > 0) return group.or;
    return [];
  }

  /** Get only direct CONDITION children (uiType = 0) */
  getDirectConditions(group: FilterCriteriaUi): FilterCriteriaUi[] {
    return this.getGroupChildren(group).filter(c => c.uiType === FilterCriteriaUiType.condition);
  }

  /** Get only direct SUB-GROUP children (uiType = 1, i.e. join) */
  getSubGroups(group: FilterCriteriaUi): FilterCriteriaUi[] {
    return this.getGroupChildren(group).filter(c => c.uiType === FilterCriteriaUiType.join);
  }

  /** Count all direct children of a group */
  getGroupChildCount(group: FilterCriteriaUi): number {
    return this.getGroupChildren(group).length;
  }

  /** Get CSS class for group depth */
  getGroupDepthClass(depth: number, group?: FilterCriteriaUi): string {
    if (group) {
      return this.isAndGroup(group) ? 'qb-group-and' : 'qb-group-or';
    }
    return 'qb-group-and';
  }

  /** Get flat enum value items from a criteria's valueList */
  getEnumItems(child: FilterCriteriaUi): any[] {
    if (!child.valueList) return [];
    const valuesGroup = child.valueList.find(g => g.name === 'values');
    return valuesGroup?.items || [];
  }

  /** Handle enum dropdown selection */
  onEnumSelect(child: FilterCriteriaUi): void {
    if (child.cascadeValueSelect) {
      if (this.enumValuesType == 'technicalLabel') {
        child.value = child.cascadeValueSelect.technicalLabel;
      } else {
        child.value = child.cascadeValueSelect.key;
      }
    } else {
      child.value = null;
    }
    this.handleChange();
  }

  /** Handle date option dropdown selection */
  onDateOptionSelect(child: FilterCriteriaUi): void {
    const option = child.selectedDateOption;

    // Reset values
    child.value = null;
    child.valueTo = null;
    child.dateValue = null;
    child.dateValueTo = null;
    child.selectManual = false;
    child.selectManualValueTo = false;

    if (!option) {
      this.handleChange();
      return;
    }

    // Preserve the user's current operation when selecting a date preset.
    // Only override when the current operation is genuinely incompatible with the preset
    // (e.g. dateRange requires a two-bound operator; current* presets inject a BT range of variables).
    const currentOp = child.operation;
    const isRangeOp = currentOp === 'BT' || currentOp === 'NB';

    switch (option) {
      case 'currentDay':
        child.value = '${D-TODAY}';
        if (isRangeOp) {
          // Single-variable preset cannot satisfy a range operator — fall back to EQ.
          child.operation = 'EQ';
        }
        break;
      case 'currentYear':
        child.value = '${D-STARTYEAR}';
        child.valueTo = '${D-ENDYEAR}';
        // Two-variable range preset must use a range operator.
        child.operation = 'BT';
        break;
      case 'currentMonth':
        child.value = '${D-STARTMONTH}';
        child.valueTo = '${D-ENDMONTH}';
        child.operation = 'BT';
        break;
      case 'currentWeek':
        child.value = '${D-STARTWEEK}';
        child.valueTo = '${D-ENDWEEK}';
        child.operation = 'BT';
        break;
      case 'specificDay':
      case 'specificMonth':
      case 'specificYear':
        // User needs to pick a date — show manual date picker.
        // Keep the user's chosen operation UNTOUCHED — all single-date operations
        // (EQ, NE, LT, GT, LE, GE) remain valid, and BT/NB stay intact so the user
        // does not silently lose a manually selected operator. If the operator is a
        // range operator, enable the second (valueTo) picker so the user can fill both bounds.
        child.selectManual = true;
        if (isRangeOp) {
          child.selectManualValueTo = true;
        }
        break;
      case 'dateRange':
        // Range always requires two bounds.
        child.selectManual = true;
        child.selectManualValueTo = true;
        if (!isRangeOp) {
          child.operation = 'BT';
        }
        break;
    }
    this.handleChange();
  }

  /** Check if a date option requires manual date input */
  isDateOptionManual(child: FilterCriteriaUi): boolean {
    const opt = child.selectedDateOption;
    return opt === 'specificDay' || opt === 'specificMonth' || opt === 'specificYear' || opt === 'dateRange';
  }

  /** Check if root has no conditions at all */
  isRootEmpty(): boolean {
    return this.getGroupChildCount(this.filterCriteriaUiRoot) === 0;
  }

  /** Toggle AND/OR logic on a specific group */
  toggleGroupLogic(group: FilterCriteriaUi): void {
    if (group.and && group.and.length > 0) {
      group.or = group.and;
      group.and = [];
    } else {
      group.and = group.or;
      group.or = [];
    }
    this.refreshUi();
    this.handleChange();
  }

  /** Add a new condition to a specific group — defaults operation to EQ */
  addConditionToGroup(group: FilterCriteriaUi): void {
    const newCondition = FilterCriteriaUi.createUi("", "", FilterCriteria.cOperatorEqual, null, null, group.level || 0, FilterCriteriaUiType.condition);
    if (group.and && group.and.length > 0) {
      group.and.push(newCondition);
    } else if (group.or && group.or.length > 0) {
      group.or.push(newCondition);
    } else {
      // Empty group - default to AND
      group.and.push(newCondition);
    }
    this.selectedCriteria = newCondition;
    this.refreshUi();
    this.handleChange();
  }

  /** Add a new sub-group to a specific group */
  addSubGroupToGroup(group: FilterCriteriaUi): void {
    const newGroup = FilterCriteriaUi.createUi("", "", "", null, null, (group.level || 0) + 1, FilterCriteriaUiType.join);
    // Add one condition inside the new group — defaults operation to EQ
    newGroup.and.push(FilterCriteriaUi.createUi("", "", FilterCriteria.cOperatorEqual, null, null, (group.level || 0) + 1, FilterCriteriaUiType.condition));

    if (group.and && group.and.length > 0) {
      group.and.push(newGroup);
    } else if (group.or && group.or.length > 0) {
      group.or.push(newGroup);
    } else {
      group.and.push(newGroup);
    }
    this.refreshUi();
    this.handleChange();
  }

  /** Delete a group (remove it from its parent) */
  deleteGroup(group: FilterCriteriaUi): void {
    this.removeNodeFromTree(group, this.filterCriteriaUiRoot);
    this.refreshUi();
    this.handleChange();
  }

  /** Recursively remove a node from the tree */
  private removeNodeFromTree(target: FilterCriteriaUi, parent: FilterCriteriaUi): boolean {
    if (parent.and) {
      const idx = parent.and.indexOf(target);
      if (idx >= 0) {
        parent.and.splice(idx, 1);
        return true;
      }
      for (const child of parent.and) {
        if (child.uiType === FilterCriteriaUiType.join && this.removeNodeFromTree(target, child)) return true;
      }
    }
    if (parent.or) {
      const idx = parent.or.indexOf(target);
      if (idx >= 0) {
        parent.or.splice(idx, 1);
        return true;
      }
      for (const child of parent.or) {
        if (child.uiType === FilterCriteriaUiType.join && this.removeNodeFromTree(target, child)) return true;
      }
    }
    return false;
  }

  /** Count ready/configured conditions in a group (recursive) */
  countReadyConditions(group: FilterCriteriaUi): number {
    let count = 0;
    const children = this.getGroupChildren(group);
    for (const child of children) {
      if (child.uiType === FilterCriteriaUiType.condition) {
        if (child.field && child.operation) count++;
      } else if (child.uiType === FilterCriteriaUiType.join) {
        count += this.countReadyConditions(child);
      }
    }
    return count;
  }


  private _convertFilterCriteriaToQlUi(criterion: FilterCriteria, level: number = 0) {

    const attribute = MvsFilterHelper.convertAttributeToFilterAttribute(criterion.field, this.form);

    let parentObjectType: string = '';
    if(criterion?.field) {
      const formField = Object.values(this.form.formFields).find(ff => ff.id === criterion.field);
      if (formField) {
        parentObjectType = this.form.getObjectTypeFromField(formField);
      }
    }

    const filterCriteriaUi = this.buildConditionUi(criterion, level, attribute, parentObjectType);
    // check if there are nested conditions
    if (criterion?.and?.length) {
      filterCriteriaUi.and = [];

      for (let nestedCriterion of criterion.and) {
        const nestedFilterCriteriaUi = this._convertFilterCriteriaToQlUi(nestedCriterion, level + 1);
        filterCriteriaUi.and.push(nestedFilterCriteriaUi);
      }
    } else if (criterion?.or?.length) {
      filterCriteriaUi.or = [];

      for (let nestedCriterion of criterion.or) {
        const nestedFilterCriteriaUi = this._convertFilterCriteriaToQlUi(nestedCriterion, level + 1);
        filterCriteriaUi.or.push(nestedFilterCriteriaUi);
      }
    }

    return filterCriteriaUi;
  }

  private _convertFilterCriteriaToListUi(criterion: FilterCriteria, level: number = 0) {
    const fieldPaths = criterion.field.split('.');
    let field: string, joinedAttribute: string = '', form: MvsFormDto;

    if (fieldPaths.length == 1) {
      field = criterion.field;
    } else if (fieldPaths.length == 2) {
      joinedAttribute = fieldPaths[0];
      field = fieldPaths[1];
    }

    if (joinedAttribute) {
      // attribute is from a subform
      form = this.getSubForm(joinedAttribute);
    } else {
      // if no joined attribute then we are getting attribute from main form
      form = this.form;
    }

    const attribute = MvsFilterHelper.convertAttributeToFilterAttribute(field, form, joinedAttribute);
    const filterCriteriaUi = this.buildConditionUi(criterion, level, attribute, form?.objectType);

    // check if there are nested conditions
    if (criterion.and.length) {
      filterCriteriaUi.and = [];

      for (let nestedCriterion of criterion.and) {
        const nestedFilterCriteriaUi = this._convertFilterCriteriaToListUi(nestedCriterion, level + 1);
        filterCriteriaUi.and.push(nestedFilterCriteriaUi);
      }
    } else if (criterion.or.length) {
      filterCriteriaUi.or = [];

      for (let nestedCriterion of criterion.or) {
        const nestedFilterCriteriaUi = this._convertFilterCriteriaToListUi(nestedCriterion, level + 1);
        filterCriteriaUi.or.push(nestedFilterCriteriaUi);
      }
    }


    return filterCriteriaUi;
  }

  private buildConditionUi (
      criterion: FilterCriteria,
      level: number,
      attribute,
      parentObjectType: string
  ): FilterCriteriaUi {
    let filterCriteriaUi: FilterCriteriaUi;

    // Distinguish between:
    // 1. Empty field with children = JOIN node (parentheses wrapper)
    // 2. Empty field without children = incomplete CONDITION (placeholder for user to fill)
    const hasChildren = (criterion.and?.length > 0) || (criterion.or?.length > 0);

    if (criterion.field == '' && hasChildren) {
      // if the field is empty AND has children, use filter type join (parentheses wrapper)
      filterCriteriaUi = FilterCriteriaUi.createUi(criterion.field, "", criterion.operation, criterion.value, criterion.valueTo, level, FilterCriteriaUiType.join);
    } else if (criterion.field == '' && !hasChildren) {
      // Empty field without children = incomplete condition (placeholder)
      // Create as CONDITION type so it shows input fields for user to fill
      filterCriteriaUi = FilterCriteriaUi.createUi(criterion.field, "", criterion.operation, criterion.value, criterion.valueTo, level, FilterCriteriaUiType.condition);
      filterCriteriaUi.parentObjectType = parentObjectType;
    } else {
      // if the field is not empty we will use filter type condition
      filterCriteriaUi = FilterCriteriaUi.createUi(criterion.field, attribute.label, criterion.operation, criterion.value, criterion.valueTo, level, FilterCriteriaUiType.condition);
      filterCriteriaUi.parentObjectType = parentObjectType;

      // BUG FIX: During restoration, onAttributeItemsSelect() reads/writes this.selectedCriteria
      // to populate valueList. We temporarily point it at the chip being restored so that
      // helper works, but we MUST restore the previous reference afterwards and write all
      // restored state directly to `filterCriteriaUi` — never through `this.selectedCriteria` —
      // to prevent cross-contamination between chips when multiple conditions are being restored.
      const previousSelectedCriteria = this.selectedCriteria;
      this.selectedCriteria = filterCriteriaUi;
      this.popUpType = 'attributeItems';
      this.onAttributeItemsSelect();
      filterCriteriaUi.value = criterion.value;
      filterCriteriaUi.valueTo = criterion.valueTo;
      filterCriteriaUi['isJoin'] = attribute.data.isJoin;
      filterCriteriaUi.joinObjectTypeAlias = attribute.data.objectType;


      // condition to check when variables are present
      // const option = this.selectedCriteria.valueOption;
      // const isValue = this.selectedCriteria.valueList[0].items.find(item => option == 'key' ? criterion.value == item.key : criterion.value == item.name);

      let isValue, isValueTo;

      if (filterCriteriaUi.valueList.length > 0) {
        // check if value and valueTo are present in valueList if yes then we will use cascade otherwise manual entry option
        // search across all value groups (enum values, date variables, etc.)
        for (const group of filterCriteriaUi.valueList) {
          if (!group.items) continue;

          if (!isValue) {
            if (group.name === 'dates') {
              // date variables are matched by name
              isValue = group.items.find(item => criterion.value == item.name);
            } else if (this.enumValuesType == 'technicalLabel') {
              isValue = group.items.find(item => criterion.value == item.technicalLabel);
            } else {
              isValue = group.items.find(item => criterion.value == item.key);
            }
          }

          if (!isValueTo) {
            if (group.name === 'dates') {
              isValueTo = group.items.find(item => criterion.valueTo == item.name);
            } else if (this.enumValuesType == 'technicalLabel') {
              isValueTo = group.items.find(item => criterion.valueTo == item.technicalLabel);
            } else {
              isValueTo = group.items.find(item => criterion.valueTo == item.key);
            }
          }
        }
      }

      if (isValue) {
        filterCriteriaUi.selectManual = false;
        filterCriteriaUi.cascadeValueSelect = isValue;
      } else {
        filterCriteriaUi.selectManual = true;
      }

      if (isValueTo) {
        filterCriteriaUi.selectManualValueTo = false;
        filterCriteriaUi.cascadeValueToSelect = isValueTo;
      } else {
        filterCriteriaUi.selectManualValueTo = true;
      }

      // filterCriteriaUi.dataType = attribute[0]['dataType'];
      const attributeFilter = attribute?.data;
      filterCriteriaUi.dataType = attributeFilter.dataType;

      // Set isEnumField and isDateField flags for restored filters
      const restoredHasEnumValues = filterCriteriaUi.valueList && filterCriteriaUi.valueList.some(group => group.name === 'values' && group.items?.length > 0);
      filterCriteriaUi.isEnumField = !!restoredHasEnumValues;

      const restoredDt = attributeFilter?.dataType;
      const restoredIsDate = restoredDt === FilterAttributeDateTypeEnum.date || restoredDt === FilterAttributeDateTypeEnum.dateTime || restoredDt === FilterAttributeDateTypeEnum.dateInstant;
      filterCriteriaUi.isDateField = restoredIsDate;

      // Restore selectedDateOption from saved variable values
      if (restoredIsDate && criterion.value) {
        const varMap: { [key: string]: string } = {
          '${D-TODAY}': 'currentDay',
          '${D-STARTYEAR}': 'currentYear',
          '${D-STARTMONTH}': 'currentMonth',
          '${D-STARTWEEK}': 'currentWeek',
        };
        const mappedOption = varMap[criterion.value];
        if (mappedOption) {
          filterCriteriaUi.selectedDateOption = mappedOption;
          filterCriteriaUi.selectManual = false;
        } else {
          // Specific date value — determine option from operation
          if (criterion.operation === 'BT' && criterion.valueTo) {
            filterCriteriaUi.selectedDateOption = 'dateRange';
          } else {
            filterCriteriaUi.selectedDateOption = 'specificDay';
          }
          filterCriteriaUi.selectManual = true;
          filterCriteriaUi.selectManualValueTo = criterion.operation === 'BT';
        }
      }

      if (attributeFilter['joinObjectTypeAlias'] || attributeFilter['joinObjectTypeId']) {

        filterCriteriaUi.joinObjectTypeAlias = attributeFilter['joinObjectTypeAlias'];
        filterCriteriaUi.joinObjectTypeId = attributeFilter['joinObjectTypeId'];
      }


      if (filterCriteriaUi.dataType == FilterAttributeDateTypeEnum.dateTime || filterCriteriaUi.dataType == FilterAttributeDateTypeEnum.date || filterCriteriaUi.dataType == FilterAttributeDateTypeEnum.dateInstant) {
        // Only set dateValue if the value is a valid date string
        if (filterCriteriaUi.value) {
          const parsedDate = new Date(filterCriteriaUi.value);
          filterCriteriaUi.dateValue = !isNaN(parsedDate.getTime()) ? parsedDate : null;
        } else {
          filterCriteriaUi.dateValue = null;
        }

        if (filterCriteriaUi.valueTo) {
          const parsedDateTo = new Date(filterCriteriaUi.valueTo);
          filterCriteriaUi.dateValueTo = !isNaN(parsedDateTo.getTime()) ? parsedDateTo : null;
        } else {
          filterCriteriaUi.dateValueTo = null;
        }

      }

      // BUG FIX: Restore the previous selectedCriteria reference so subsequent chip
      // restorations do not accidentally reuse this chip as the shared-state target.
      this.selectedCriteria = previousSelectedCriteria;
    }

    return filterCriteriaUi;
  }
}
